import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    Box,
    alpha,
} from '@mui/material';

const CreateOrganizationDialog = ({ open, onClose, onSubmit, colors, t }) => {
    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setName('');
            setDomain('');
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setLoading(true);
        try {
            const data = { name: name.trim() };
            if (domain.trim()) {
                data.domain = domain.split(',').map((d) => d.trim()).filter(Boolean);
            }
            await onSubmit(data);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
                {t('create_organization', 'Create Organization')}
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 2 }}>
                <DialogContentText sx={{ mb: 3, color: colors.textSecondary }}>
                    {t('create_org_description', 'Create a new organization to onboard a client.')}
                </DialogContentText>
                <TextField
                    autoFocus
                    label={t('organization_name', 'Organization Name')}
                    fullWidth
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <TextField
                    label={t('email_domains', 'Email Domains (comma-separated)')}
                    fullWidth
                    variant="outlined"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="@acme.com, @acme.org"
                    helperText={t('domain_help', 'Optional. Users with matching email domains will auto-join this organization.')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                <Button onClick={onClose} sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={!name.trim() || loading}
                    sx={{
                        bgcolor: colors.primary, color: 'white', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
                        '&:hover': { bgcolor: colors.primaryDark },
                        '&.Mui-disabled': { bgcolor: alpha(colors.primary, 0.6), color: 'white' },
                    }}
                >
                    {loading ? t('creating', 'Creating...') : t('create', 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateOrganizationDialog;
